/* eslint-disable no-undef */
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import LogoDark from '../../images/logo/gitme-logo-white.svg';
import { Skeleton } from '../../components/ui';
import {
  PASSWORD_ERROR_MESSAGE,
  PASSWORD_REQUIREMENTS,
  getPasswordStrength,
  getPasswordStrengthText,
  validatePassword
} from '../../utils/passwordValidation';

const MAX_PASSWORD_LENGTH = 128;
const isValidEmail = value =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [email] = useState(() => searchParams.get('email') || '');
  const [token] = useState(() => searchParams.get('token') || '');

  useEffect(() => {
    if (searchParams.toString()) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [searchParams]);

  const validateForm = () => {
    const newErrors = {};

    if (!newPassword || typeof newPassword !== 'string') {
      newErrors.newPassword = 'Password is required';
    } else if (!validatePassword(newPassword)) {
      newErrors.newPassword = PASSWORD_ERROR_MESSAGE;
    } else if (newPassword.length > MAX_PASSWORD_LENGTH) {
      newErrors.newPassword = `Password must be at most ${MAX_PASSWORD_LENGTH} characters`;
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleReset = async () => {
    if (!token || !isValidEmail(email)) {
      toast.error('Invalid or expired reset link.');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/reset-password`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, newPassword })
      });

      const data = await res.json();

      if (res.ok) {
        toast.success('Your password has been reset.');
        navigate('/login');
      } else {
        const errorMessage =
          typeof data?.message === 'string' &&
          /invalid|expired|too many|password/i.test(data.message)
            ? data.message
            : 'Reset failed. Please request a new reset link.';
        toast.error(errorMessage);
      }
    } catch (_err) {
      toast.error('An error occurred.');
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = getPasswordStrength(newPassword);
  const passwordStrengthInfo = getPasswordStrengthText(passwordStrength);

  return (
    <div className="min-h-screen flex bg-gray-100">
      <div className="w-full md:w-1/2 flex items-center justify-center p-10 bg-white">
        <div className="w-full max-w-md">
          <h2 className="text-3xl font-semibold text-slate-900 mb-2">
            Reset Your Password
          </h2>
          <p className="mb-8 text-sm text-slate-500">
            Create a new secure password for your account.
          </p>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                New Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <LockIcon className="h-5 w-5 text-slate-400" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={e => {
                    setNewPassword(
                      e.target.value.slice(0, MAX_PASSWORD_LENGTH)
                    );
                    setErrors(prev => ({ ...prev, newPassword: '' }));
                  }}
                  placeholder="Create a strong password"
                  autoComplete="new-password"
                  maxLength={MAX_PASSWORD_LENGTH}
                  className={`w-full pl-10 pr-12 py-3 border rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    errors.newPassword ? 'border-red-500' : 'border-slate-300'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  disabled={loading}
                >
                  {showPassword ? (
                    <VisibilityOffIcon className="h-5 w-5 text-slate-400 hover:text-slate-600" />
                  ) : (
                    <VisibilityIcon className="h-5 w-5 text-slate-400 hover:text-slate-600" />
                  )}
                </button>
              </div>

              {newPassword && (
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-slate-600">
                      Password Strength
                    </span>
                    <span
                      className={`text-xs font-medium px-2 py-1 rounded ${passwordStrengthInfo.color} text-white`}
                    >
                      {passwordStrengthInfo.text}
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${passwordStrengthInfo.color}`}
                      style={{
                        width: `${(passwordStrength / 5) * 100}%`
                      }}
                    ></div>
                  </div>
                </div>
              )}

              {errors.newPassword && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.newPassword}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <LockIcon className="h-5 w-5 text-slate-400" />
                </div>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => {
                    setConfirmPassword(
                      e.target.value.slice(0, MAX_PASSWORD_LENGTH)
                    );
                    setErrors(prev => ({ ...prev, confirmPassword: '' }));
                  }}
                  placeholder="Confirm your new password"
                  autoComplete="new-password"
                  maxLength={MAX_PASSWORD_LENGTH}
                  className={`w-full pl-10 pr-12 py-3 border rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    errors.confirmPassword
                      ? 'border-red-500'
                      : 'border-slate-300'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  disabled={loading}
                >
                  {showConfirmPassword ? (
                    <VisibilityOffIcon className="h-5 w-5 text-slate-400 hover:text-slate-600" />
                  ) : (
                    <VisibilityIcon className="h-5 w-5 text-slate-400 hover:text-slate-600" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.confirmPassword}
                </p>
              )}
            </div>

            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="text-sm font-medium text-slate-700 mb-2">
                Password Requirements:
              </h4>
              <ul className="text-xs text-slate-600 space-y-1">
                {PASSWORD_REQUIREMENTS.map(item => (
                  <li key={item}>• {item}</li>
                ))}
              </ul>
            </div>

            <button
              onClick={handleReset}
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 disabled:from-slate-400 disabled:to-slate-500 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl disabled:shadow-none disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Skeleton className="h-3 w-24 rounded-sm bg-white/50" />
                  Resetting...
                </>
              ) : (
                <>
                  Reset Password
                  <ArrowForwardIcon className="h-5 w-5" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="hidden md:flex w-1/2 bg-blue-600 items-center justify-center p-10 rounded-l-[2rem] flex-col text-center text-white">
        <img
          src={LogoDark}
          alt="Logo"
          className="mb-6"
          style={{ width: '10rem' }}
        />
        <p className="text-lg font-semibold mb-2 max-w-md">
          A New Standard in Developer Performance Analytics.
        </p>
        <p className="text-sm font-light max-w-md leading-relaxed">
          Gitme leverages advanced AI to deliver accurate, unified, and
          transparent insights, replacing fragmented metrics with a single
          source of truth.
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;
